// src/merch/persist.js
//
// Autosave the current design to IndexedDB (images are data URLs — too
// big for localStorage). Restores on load, so a refresh or coming back
// from a cancelled checkout never loses a kid's masterpiece.

import { subscribe, snapshot } from "valtio";
import { merch } from "./store";
import { getProduct, getSize, getSides } from "./products";
import { resetHistory } from "./history";

const DB = "merchally";
const STORE = "kv";
const KEY = "design:v2"; // v2: zone-based sides (shaft/blade × front/back)

function db() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function put(value) {
  const d = await db();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, KEY);
    tx.oncomplete = res;
    tx.onerror = () => rej(tx.error);
  });
}
async function get() {
  const d = await db();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, "readonly");
    const q = tx.objectStore(STORE).get(KEY);
    q.onsuccess = () => res(q.result || null);
    q.onerror = () => rej(q.error);
  });
}

function pick(s) {
  return {
    productId: s.productId, sizeId: s.sizeId, colourId: s.colourId, hand: s.hand, saved: s.saved,
    side: s.side, layers: s.layers, sideLayers: s.sideLayers,
  };
}

let started = false;

/** Restore the saved design, then keep saving (debounced). */
export async function startAutosave({ restore = true } = {}) {
  if (started) return;
  if (typeof indexedDB === "undefined") { merch.loaded = true; return; }
  started = true;
  try {
    const saved = restore ? await get() : null;
    if (saved?.layers) {
      Object.assign(merch, {
        productId: saved.productId ?? merch.productId,
        sizeId: saved.sizeId ?? merch.sizeId,
        colourId: saved.colourId ?? merch.colourId,
        hand: saved.hand === "left" ? "left" : "right",
        saved: saved.saved || null,
        side: (() => {
          const p = getProduct(saved.productId);
          const zs = getSides(p, getSize(p, saved.sizeId));
          return zs.some((z) => z.id === saved.side) ? saved.side : zs[0].id;
        })(),
        layers: saved.layers || [],
        sideLayers: saved.sideLayers || {},
        selectedId: null,
      });
      merch.sideRev++;
      merch.rev++;
      resetHistory(); // undo shouldn't wipe the restored design
    }
  } catch (e) {
    console.warn("[merch] restore failed", e);
  }
  merch.loaded = true; // defaults (e.g. the ornament jersey) may go in now

  let t = null;
  let last = merch.rev;
  subscribe(merch, () => {
    if (merch.rev === last) return; // ignore selection / paint-only changes
    last = merch.rev;
    clearTimeout(t);
    t = setTimeout(() => {
      put(JSON.parse(JSON.stringify(pick(snapshot(merch))))).catch((e) => console.warn("[merch] autosave failed", e));
    }, 800);
  });
}

/** Wipe the saved design (e.g. "Start over" or after a completed order). */
export async function clearSavedDesign() {
  try { await put(null); } catch { /* ignore */ }
}
